import { Menu } from "@ark-ui/react";
import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";

interface BaseItem {
  value: string;
  disabled?: boolean;
}

export interface IconItem extends BaseItem {
  icon: IconName;
}

export interface LabelIconItem extends IconItem {
  label: string;
}

interface ContextualMenuSection {
  title?: string;
  items: LabelIconItem[] | IconItem[];
}

interface BaseContextualMenuProps extends Omit<Menu.RootProps, "children"> {
  trigger: React.ReactNode;
  zIndex?: string;
  portalled?: boolean;
  asSubmenu?: boolean;
  actionLabel?: string;
  secondaryActionLabel?: string;
  menuContentClassName?: string;
  onAction?: (event: React.MouseEvent<HTMLElement>, values?: string[]) => void;
  onSecondaryAction?: (
    event: React.MouseEvent<HTMLElement>,
    values?: string[],
  ) => void;
}

export interface ContextualMenuPropsWithSections
  extends BaseContextualMenuProps {
  items?: never;
  sections: ContextualMenuSection[];
  children?: never;
  onSetValue?: (event: React.MouseEvent<HTMLElement>, value: string) => void;
}

export interface ContextualMenuPropsWithItems extends BaseContextualMenuProps {
  items: LabelIconItem[] | IconItem[];
  sections?: never;
  children?: never;
  onSetValue?: (event: React.MouseEvent<HTMLElement>, value: string) => void;
}

export interface ContextualMenuPropsWithChildren
  extends BaseContextualMenuProps {
  items?: never;
  sections?: never;
  children: React.ReactNode;
  onSetValue?: never;
}

const isBaseItem = (item: unknown): item is BaseItem => {
  return (
    !!item &&
    typeof item === "object" &&
    "value" in item &&
    typeof item.value === "string"
  );
};

const isIconItem = (item: LabelIconItem | IconItem): item is IconItem => {
  return isBaseItem(item) && "icon" in item && !("label" in item);
};

export const isLabelIconItem = (
  item: LabelIconItem | IconItem,
): item is LabelIconItem => {
  return isIconItem(item) && "label" in item;
};
